import { createTheme } from "@mui/material/styles";
import typography from "./Typography";
import { shadows } from "./Shadows";

// --- Brand tokens -----------------------------------------------------------
const BRAND = '#5D87FF';
const BRAND_DARK = '#4570EA';
// Neutral grey used for INPUT focus states only (not the global a11y focus ring,
// which stays brand blue). Applied to MUI inputs here, plus CustomTextField and
// the chat-textarea wrapper (ChatInput.jsx).
const INPUT_FOCUS = '#7C8FAC';
// Opt-in brand gradient (use <Button className="gradient-btn">) for hero CTAs.
const gradient =
  'linear-gradient(6deg, rgba(159,179,236,1) 0%, rgba(93,135,255,1) 87%, rgba(93,135,255,1) 100%)';
const gradientHover =
  'linear-gradient(6deg, rgba(93,135,255,1) 0%, rgba(159,179,236,1) 87%, rgba(159,179,236,1) 100%)';

// --- Mode-agnostic component overrides --------------------------------------
const commonComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      '*:focus-visible': {
        outline: `2px solid ${BRAND}`,
        outlineOffset: '2px',
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 10,
        fontWeight: 600,
        textTransform: 'none',
      },
      containedPrimary: {
        backgroundColor: BRAND,
        color: '#ffffff',
        '&:hover': { backgroundColor: BRAND_DARK },
        // Opt-in brand gradient: <Button className="gradient-btn" ... />
        '&.gradient-btn': {
          background: `${gradient}!important`,
          '&:hover': { background: `${gradientHover}!important` },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        // Input focus uses a neutral grey, not the primary blue.
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: INPUT_FOCUS,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { '&.Mui-focused': { color: INPUT_FOCUS } },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16, backgroundImage: 'none' },
    },
  },
};

const baselightTheme = createTheme({
  direction: 'ltr',
  shape: { borderRadius: 12 },
  palette: {
    mode: 'light',
    primary: {
      main: '#5D87FF',
      light: '#ECF2FF',
      dark: '#4570EA',
      zosa: '#5D87FF',
    },
    secondary: {
      main: '#49BEFF',
      light: '#E8F7FF',
      dark: '#23afdb',
    },
    success: {
      main: '#12a68a',
      light: '#E6FFFA',
      dark: '#02b3a9',
      contrastText: '#ffffff',
    },
    info: {
      main: '#539BFF',
      light: '#EBF3FE',
      dark: '#1682d4',
      contrastText: '#ffffff',
    },
    error: {
      main: '#FA896B',
      light: '#FDEDE8',
      dark: '#f3704d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFAE1F',
      light: '#FEF5E5',
      dark: '#ae8e59',
      contrastText: '#ffffff',
    },
    purple: {
      A50: '#EBF3FE',
      A100: '#6610f2',
      A200: '#557fb9',
    },
    grey: {
      100: '#F2F6FA',
      200: '#EAEFF4',
      300: '#DFE5EF',
      400: '#7C8FAC',
      500: '#4f5d75',
      600: '#2A3547',

    },
    background: {
      defaulttransparent:"#FFFFFF82",
    },
    text: {
      primary: '#2A3547',
      secondary: '#7C8FAC',
    },
    action: {
      disabledBackground: 'rgba(73,82,88,0.12)',
      hoverOpacity: 0.02,
      hover: '#f6f9fc',
    },
    divider: '#e5eaef',
    zosa: {
      primary: '#5D87FF',
    }
  },
  typography,
  shadows,
  components: {
    ...commonComponents,
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(93,135,255,0.12)',
            color: BRAND,
            '&:hover': {
              backgroundColor: 'rgba(93,135,255,0.18)',
            },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 9 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #e5eaef',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        colorDefault: {
          backgroundColor: 'rgba(93,135,255,0.12)',
          color: BRAND,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({
          theme
        }) => ({
          [theme.breakpoints.down('sm')]: {
            boxShadow: 'none',
          },
        }),
      },
    },
  },
},

);


const baseDarkTheme = createTheme({
  direction: "ltr",
  shape: { borderRadius: 12 },
  palette: {
    mode: 'dark',
    primary: {
      main: "#fff",
      light: "#2D2D3A",
      dark: "#1D1D26",
      zosa: '#5D87FF',
    },
    secondary: {
      main: "#49BEFF",
      light: "#E8F7FF",
      dark: "#23afdb",
    },
    success: {
      main: "#12a68a",
      light: "#E6FFFA",
      dark: "#02b3a9",
      contrastText: "#ffffff",
    },
    info: {
      main: "#539BFF",
      light: "#EBF3FE",
      dark: "#1682d4",
      contrastText: "#ffffff",
    },
    error: {
      main: "#FA896B",
      light: "#FDEDE8",
      dark: "#f3704d",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#FFAE1F",
      light: "#FEF5E5",
      dark: "#ae8e59",
      contrastText: "#ffffff",
    },
    grey: {
      100: "#2D2D3A",
      200: "#1D1D26",
      300: "#121217",
      400: "#7C8FAC",
      500: "#A5A5B9",
      600: "#E0E0E3",
    },
    text: {
      primary: "#ffffff",
      secondary: "#A5A5B9",
    },
    background: {
      default: "#31313C",
      defaulttransparent:"#31313CBD",
      paper: "#31313C",
    },
    divider: "#3F3F4E",
    zosa: {
      primary: '#5D87FF',
    }
  },
  typography,
  shadows,
  components: {
    ...commonComponents,
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(93,135,255,0.18)',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(93,135,255,0.26)',
            },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 9 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #3F3F4E',
          backgroundImage: 'radial-gradient(circle, rgba(45, 45, 58, 1) 0%, rgb(49 49 60) 100%)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        colorDefault: {
          backgroundColor: 'rgba(93,135,255,0.18)',
          color: '#A9C0FF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({
          theme
        }) => ({
          // Keep the Alert surface flat against the dark background
          '&.MuiAlert-root': {
            backgroundColor: '#31313c',
          },
          // Flat surfaces on mobile (matches light mode)
          [theme.breakpoints.down('sm')]: {
            boxShadow: 'none',
          },
        }),
      },
    },
  },
});

export { baselightTheme, baseDarkTheme };
